/**
 * Lightweight capture-field validation.
 *
 * Goal: reject input that's *obviously* wrong (no country code, junk length,
 * malformed email) without pretending to be a full libphonenumber. Kept
 * dependency-free and permissive enough not to block real users.
 */

/**
 * Email sanity check: `local@domain.tld`, no spaces, a dotted domain with a 2+
 * letter TLD, and sane lengths. Deliberately not RFC-5322-exhaustive, but tight
 * enough to reject the common mistakes the loose `[^\s@]+@[^\s@]+\.[^\s@]{2,}`
 * pattern used to wave through: double "@" (`a@@b.com`), consecutive or edge
 * dots (`a@b..com`, `.a@b.com`, `a.@b.com`), and numeric-only TLDs (`a@b.12`).
 *
 * Enforces, beyond the regex:
 *   - RFC 5321 length limits (254 total, 64 for the local part)
 *   - exactly one "@" with a non-empty local and domain part
 *   - no leading / trailing / consecutive dots in either part
 *   - a domain TLD of at least two letters
 */
const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(value: string): boolean {
  const s = (value || "").trim();
  if (s.length < 6 || s.length > 254) return false;

  const at = s.lastIndexOf("@");
  if (at < 1) return false;

  const local = s.slice(0, at);
  const domain = s.slice(at + 1);

  if (local.length > 64) return false;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return false;
  }
  if (domain.includes("..")) return false;

  // TLD must be at least two letters (rejects "a@b", "a@b.1", "a@b.c").
  if (!/\.[a-zA-Z]{2,}$/.test(domain)) return false;

  return EMAIL_RE.test(s);
}

/**
 * Normalize a typed phone number toward E.164 (`+<country><national>`):
 *   - strip spaces, dashes, dots, parens
 *   - `00<cc>…` → `+<cc>…` (international call prefix)
 *   - no leading `+` but a `defaultDialCode` is configured → assume the number
 *     is national: drop a trunk "0" and prepend the dial code
 * Anything already starting with `+` is left as-is (minus formatting).
 */
export function normalizePhone(raw: string, defaultDialCode?: string): string {
  let s = (raw || "").trim().replace(/[\s().-]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (!s.startsWith("+") && defaultDialCode) {
    const dial = defaultDialCode.replace(/[^\d]/g, "");
    if (dial) s = "+" + dial + s.replace(/^0+/, "");
  }
  return s;
}

/**
 * Validate an already-normalized phone string. Requires a country code (leading
 * `+`) and an E.164-plausible length (8–15 digits total, country code never
 * starts with 0). This is the "has a country code and a sane length" check the
 * widget enforces.
 */
export function isValidPhone(e164: string): boolean {
  if (!/^\+\d+$/.test(e164)) return false; // must be "+" then digits only
  const digits = e164.slice(1);
  if (digits.length < 8 || digits.length > 15) return false;
  if (digits.startsWith("0")) return false; // no valid calling code starts with 0
  return true;
}
